import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clientMasterApi } from '@/services/api';
import { FilterAsyncMultiSelect } from '@/components/ui';
import type { FilterOption } from '@/components/ui';
import type { ClientSeriesOption } from '@/types/client';
import { describeRequestError } from '@/utils/apiErrors';
import commonStyles from './ClientCommon.module.css';

/** A series row as stored in an approval's "before" snapshot. */
interface SnapshotSeries {
  series_code: string;
  fa_account_no: string;
}

interface ClientSeriesFieldProps {
  clientId: number;
  mode: 'edit' | 'view' | 'approval';
  /** The approval's "before" series; only in approval mode, absent on older snapshots. */
  previousSeries?: SnapshotSeries[];
  /** Called with the full "Source:series_key" list once the user changes it. */
  onChange?: (seriesRefs: string[]) => void;
  disabled?: boolean;
}

// Same label the backend builds for an option, so before/after compare as text.
const snapshotLabel = (row: SnapshotSeries) => `${row.series_code} (FA ${row.fa_account_no})`;

const pickerLabel = ({ label, held_by }: ClientSeriesOption) =>
  held_by ? `${label} · held by ${held_by} other client${held_by === 1 ? '' : 's'}` : label;

/**
 * The client's series, picked like Restricted Companies. Saving the client form sends
 * the list, so a change goes into the same approval request as the client's other edits.
 */
const ClientSeriesField: React.FC<ClientSeriesFieldProps> = ({ clientId, mode, previousSeries, onChange, disabled }) => {
  const [options, setOptions] = useState<ClientSeriesOption[]>([]);
  const [selected, setSelected] = useState<FilterOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    clientMasterApi.getClientSeriesOptions(clientId)
      .then(response => {
        if (cancelled) return;
        setOptions(response);
        setSelected(response.filter(option => option.selected).map(({ value, label }) => ({ value, label })));
      })
      .catch(err => {
        if (!cancelled) setError(describeRequestError(err, 'load series'));
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const loadOptions = useCallback(async (input: string) => {
    const term = input.trim().toLowerCase();
    return options
      .filter(option => option.label.toLowerCase().includes(term))
      .map(option => ({ value: option.value, label: pickerLabel(option) }));
  }, [options]);

  const currentLabels = selected.map(option => option.label);
  const previousLabels = useMemo(() => previousSeries?.map(snapshotLabel), [previousSeries]);
  const changed = Boolean(previousLabels)
    && [...previousLabels!].sort().join('|') !== [...currentLabels].sort().join('|');

  return (
    <div className={commonStyles['client-common-section']}>
      <h3>Series</h3>
      <div className={commonStyles['client-common-grid']}>
        <div className={`${commonStyles['client-common-field']} ${commonStyles['full-width']}`}>
          {mode === 'edit' ? (
            <FilterAsyncMultiSelect
              label="Search and Select Series"
              value={selected}
              onChange={next => {
                setSelected(next);
                onChange?.(next.map(option => option.value));
              }}
              loadOptions={loadOptions}
              limitTags={5}
              disabled={disabled || Boolean(error)}
            />
          ) : (
            <>
              <label>Assigned Series</label>
              <div className={`${commonStyles['view-value']} ${changed ? commonStyles.changed : ''}`}>
                {currentLabels.length ? currentLabels.join(', ') : '—'}
                {changed && (
                  <span className={commonStyles['previous-value']}>
                    Previously: {previousLabels!.length ? previousLabels!.join(', ') : 'EMPTY'}
                  </span>
                )}
              </div>
            </>
          )}
          {error && <span className={commonStyles['client-field-error']}>{error}</span>}
        </div>
      </div>
    </div>
  );
};

export default ClientSeriesField;
